import React, { forwardRef, useMemo } from 'react';
import clsx from 'clsx';

import { ReservationsDataOutDTO } from '@api/ringil-api.v2';
import { LayoutZone, SurfaceLayout, zoneItems } from '@components/features/calendar-layout/model';
import { BUILT_IN_DETAIL_LAYOUT } from '@components/features/calendar-layout/defaults';
import { FragmentItemWrapper, FragmentZone } from '@components/features/calendar-layout/fragments/FragmentZone';
import { CalendarStateStyle } from '@components/features/calendar-layout/stateStyle';

interface CalendarEventCardDetailProps {
  data: ReservationsDataOutDTO;
  style: CalendarStateStyle;
  link: string;
  /** Zone-based layout; falls back to the built-in one. */
  layout?: SurfaceLayout;
  className?: string;
  positionStyle?: React.CSSProperties;
  onStateChange: () => void;
  /** Designer-only hooks: wrap each zone container / each rendered fragment with edit affordances. */
  zoneWrapper?: (zone: LayoutZone, node: React.ReactNode) => React.ReactNode;
  itemWrapper?: FragmentItemWrapper;
  /** Designer-only: keep empty zones visible so they can still be dropped into. */
  keepEmptyZones?: boolean;
  /** Designer-only: render in normal flow instead of absolutely positioned over the calendar. */
  inline?: boolean;
}

export const CalendarEventCardDetail = forwardRef<HTMLDivElement, CalendarEventCardDetailProps>(
  ({ data, style, layout, className, positionStyle, onStateChange, zoneWrapper, itemWrapper, keepEmptyZones, inline }, ref) => {
    const detailLayout = useMemo<SurfaceLayout>(() => layout ?? BUILT_IN_DETAIL_LAYOUT, [layout]);
    const zoneProps = { surface: 'detail' as const, layout: detailLayout, data, stateStyle: style, onStateChange, itemWrapper };

    const zone = (which: LayoutZone) => {
      const node = <FragmentZone {...zoneProps} zone={which} />;
      return zoneWrapper ? zoneWrapper(which, node) : node;
    };

    // The header is a bordered band; with nothing configured into it, it would render as a bare
    // coloured rule above the body, so drop it entirely instead. The designer keeps it, otherwise
    // an emptied header could never be dropped into again.
    const hasHeader =
      keepEmptyZones || zoneItems(detailLayout, LayoutZone.HeaderLeft).length > 0 || zoneItems(detailLayout, LayoutZone.HeaderRight).length > 0;

    return (
      <div ref={ref} style={positionStyle} className={clsx(className, 'event-card-detail cursor-default z-10', !inline && 'absolute')}>
        <div className="rounded-lg shadow-card text-content-primary w-[21.25rem] text-sm">
          {hasHeader && (
            <div className={clsx('p-4 bg-base-base gap-x-2 border-b-3 border-0 flex justify-between items-start border-solid text-base', style && style.wrapper)}>
              {/* Each side is wrapped so `justify-between` splits the header in two, however many
                  fragments a side holds — without the wrappers, three fragments would spread out. */}
              <div className="min-w-0">{zone(LayoutZone.HeaderLeft)}</div>
              <div>{zone(LayoutZone.HeaderRight)}</div>
            </div>
          )}

          <div className="bg-white relative flex flex-col gap-y-4 p-4 pb-6">
            {zone(LayoutZone.Body)}
          </div>
        </div>
      </div>
    );
  },
);

CalendarEventCardDetail.displayName = 'CalendarEventCardDetail';
